/**
 * Error logging utility for development and production
 * Integrates with Sentry for production error tracking
 */

export interface ErrorContext {
  userId?: string;
  petId?: string;
  [key: string]: unknown;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class ErrorLogger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  /**
   * Log debug message
   */
  debug(message: string, context?: ErrorContext): void {
    this.log('debug', message, context);
  }

  /**
   * Log info message
   */
  info(message: string, context?: ErrorContext): void {
    this.log('info', message, context);
  }

  /**
   * Log warning
   */
  warn(message: string, context?: ErrorContext): void {
    this.log('warn', message, context);
  }

  /**
   * Log error with automatic Sentry integration
   */
  error(message: string, error?: Error | unknown, context?: ErrorContext): void {
    this.log('error', message, context);

    // Log the error itself
    if (error instanceof Error) {
      if (this.isDevelopment) {
        console.error(`[Error] ${message}:`, error);
      }
      this.sendToSentry(error, message, context);
    } else if (error) {
      const err = new Error(String(error));
      this.sendToSentry(err, message, context);
    }
  }

  /**
   * Log API errors
   */
  apiError(endpoint: string, status: number, response?: unknown, context?: ErrorContext): void {
    const message = `API Error: ${endpoint} (${status})`;
    this.error(message, undefined, {
      ...context,
      endpoint,
      status,
      response: typeof response === 'object' ? JSON.stringify(response).slice(0, 200) : response,
    });
  }

  /**
   * Log validation errors
   */
  validationError(field: string, value: unknown, message: string, context?: ErrorContext): void {
    this.warn(`Validation Error: ${field}`, {
      ...context,
      field,
      value: String(value).slice(0, 100),
      reason: message,
    });
  }

  /**
   * Internal logging
   */
  private log(level: LogLevel, message: string, context?: ErrorContext): void {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (this.isDevelopment) {
      const consoleMethod = level === 'error' ? console.error : console[level];
      consoleMethod(`${prefix} ${message}`, context || '');
    }
  }

  /**
   * Send error to Sentry (when configured)
   */
  private sendToSentry(error: Error, message: string, context?: ErrorContext): void {
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;

      Sentry.captureException(error, {
        tags: {
          source: 'app',
        },
        contexts: {
          app: context,
        },
        message,
      });
    }
  }
}

// Export singleton
export const errorLogger = new ErrorLogger();

/**
 * Global error handler for unhandled promise rejections
 */
export function setupGlobalErrorHandling(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    errorLogger.error('Uncaught error', event.error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    errorLogger.error('Unhandled promise rejection', event.reason);
  });
}
